import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useCallback, useState } from 'react';
import { OrderStatusEnum, OrderTypeEnum } from '@restavo/shared/enums/order';
import { useTenantContext } from 'src/context/useTenantContext';
import { useUrlSearchFilter } from 'src/hooks/useUrlSearchFilter';
import { useDebounce } from 'src/hooks/useUtils';
import {
  tenantOrderCreate,
  tenantOrderUpdate,
  tenantOrders,
  tenantOrdersDelivery,
  tenantOrdersInProgress,
  tenantOrdersRecent,
  tenantPaidOrderTokens,
} from 'src/services/apis/tenantRequests';
import { OrderCreateDto, OrderEntity, OrderUpdateDto } from 'src/services/apis/types';
import { QUERIES } from 'src/utils/react-query';

export const useRecentOrders = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId],
    () => tenantOrdersRecent({ orgSlug, restaurantId, branchId }, { limit: 30 }),
    {
      cacheTime: 0,
      refetchOnMount: true,
      refetchInterval: 10000,
    }
  );
};

export const useDeliveryOrders = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.DELIVERY_ORDERS, orgSlug, restaurantId, branchId],
    () => tenantOrdersDelivery({ orgSlug, restaurantId, branchId }, { limit: 50 }),
    {
      cacheTime: 0,
      refetchOnMount: true,
      refetchInterval: 10000,
    }
  );
};

export const useInProgressOrders = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.IN_PROGRESS_ORDERS, orgSlug, restaurantId, branchId],
    () => tenantOrdersInProgress({ orgSlug, restaurantId, branchId }, { limit: 100 }),
    {
      cacheTime: 0,
      refetchOnMount: true,
      refetchInterval: 5000,
    }
  );
};
export const usePaidOrderTokens = () => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useQuery(
    [QUERIES.PAID_ORDER_TOKENS, orgSlug, restaurantId, branchId],
    () => tenantPaidOrderTokens({ orgSlug, restaurantId, branchId }),
    {
      cacheTime: 0,
      refetchOnMount: true,
      refetchInterval: 1000,
      refetchOnWindowFocus: true,
    }
  );
};

export const useOrders = (today: boolean) => {
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const urlSearch = useUrlSearchFilter();
  const [page, setPage] = useState(1);
  const [filters, setFilters] = useState<{
    limit: number;
    type: string | null;
    status: string | null;
    search: string | null;
    startDate: string | null;
    endDate: string | null;
  }>({
    limit: 20,
    type: null,
    status: null,
    search: urlSearch,
    startDate: today ? dayjs().startOf('day').toISOString() : null,
    endDate: today ? dayjs().endOf('day').toISOString() : null,
  });
  const getDefaultFilters = useCallback(() => {
    return {
      limit: 20,
      type: null,
      status: null,
      search: null,
      startDate: today ? dayjs().startOf('day').toISOString() : null,
      endDate: today ? dayjs().endOf('day').toISOString() : null,
    };
  }, [today]);
  // Reset the filters when the scope changes, derived during render rather than in an
  // effect: React re-renders with the new value before committing, where the effect
  // committed the stale filters first and then immediately replaced them. The URL's
  // ?search= term (if the new scope was opened with one) survives the reset so a
  // palette deep-link keeps filtering the page it landed on.
  const filterScope = String(today);
  const [previousFilterScope, setPreviousFilterScope] = useState(filterScope);
  if (previousFilterScope !== filterScope) {
    setPreviousFilterScope(filterScope);
    setFilters({ ...getDefaultFilters(), search: urlSearch });
  }
  const debounceSearch = useDebounce(filters.search, 1000);
  const query = useQuery(
    [
      QUERIES.ORDERS,
      orgSlug,
      restaurantId,
      branchId,
      page,
      {
        limit: filters.limit,
        type: filters.type,
        status: filters.status,
        search: debounceSearch,
        startDate: filters.startDate,
        endDate: filters.endDate,
      },
    ],
    ({ queryKey: [_key, _orgSlug, _restaurantId, _branchId, _page, _filters] }: { queryKey: any }) =>
      tenantOrders(
        { orgSlug: _orgSlug as string, restaurantId: _restaurantId as string, branchId: _branchId as string },
        {
          page: _page as number,
          limit: _filters.limit,
          type: _filters.type ? Number(_filters.type) : undefined,
          status: _filters.status ? Number(_filters.status) : undefined,
          search: _filters.search,
          startDate: _filters.startDate,
          endDate: _filters.endDate,
        }
      ),
    {
      cacheTime: 0,
      refetchOnMount: true,
      refetchInterval: 30000,
      keepPreviousData: true,
      refetchOnWindowFocus: false,
    }
  );
  return {
    query,
    page,
    filters,
    setPage,
    setFilters,
  };
};

export const useCreateOrdersMutation = () => {
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useMutation((payload: OrderCreateDto) => tenantOrderCreate({ orgSlug, restaurantId, branchId }, payload), {
    onSuccess: async (order: OrderEntity) => {
      await queryClient.cancelQueries({ queryKey: [QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId] });
      const previousRecentOrders = queryClient.getQueryData([QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId]);
      if (previousRecentOrders) {
        queryClient.setQueryData([QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId], (old: any) => ({
          ...old,
          total: old.total + 1,
          data: [...old.data, order],
        }));
        return { previousRecentOrders };
      }
      await queryClient.refetchQueries([QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId]);
    },
    onError: () => {
      queryClient.invalidateQueries([QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId]).then((r) => r);
    },
    onSettled: () => {
      queryClient.invalidateQueries([QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId]).then((r) => r);
    },
    retry: false,
  });
};

export const useUpdateOrdersMutation = (type?: OrderTypeEnum) => {
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const updateQueries =
    type && type === OrderTypeEnum.DELIVERY
      ? [QUERIES.DELIVERY_ORDERS, orgSlug, restaurantId, branchId]
      : [QUERIES.RECENT_ORDERS, orgSlug, restaurantId, branchId];
  return useMutation(
    (payload: OrderUpdateDto) => tenantOrderUpdate({ orgSlug, restaurantId, branchId }, payload.id, payload),
    {
      onMutate: async (data) => {
        const previousOrders = queryClient.getQueryData(updateQueries);
        await queryClient.cancelQueries({ queryKey: updateQueries });
        if (data.status && [OrderStatusEnum.PAID, OrderStatusEnum.CANCELLED].includes(data.status)) {
          queryClient.setQueryData(updateQueries, (old: any) => ({
            ...old,
            data: old.data.filter((item) => item.id !== data.id),
          }));
          return { previousOrders };
        }
        if (data.orderItems && data.orderItems?.length > 0) {
          return;
        }
        queryClient.setQueryData(updateQueries, (old: any) => ({
          ...old,
          data: old.data.map((item) => (item.id === data.id ? { ...item, ...data } : item)),
        }));
        return { previousOrders };
      },
      onSuccess: async (order: OrderEntity, variables) => {
        const previousOrders = queryClient.getQueryData(updateQueries);
        if (!variables.orderItems || variables.orderItems?.length === 0) {
          return;
        }
        await queryClient.cancelQueries({ queryKey: updateQueries });
        queryClient.setQueryData(updateQueries, (old: any) => ({
          ...old,
          data: old.data.map((item) => (item.id === order.id ? { ...order } : item)),
        }));
        return { previousOrders };
      },
      onError: () => {
        return queryClient.refetchQueries(updateQueries);
      },
    }
  );
};
export const useMarkAsReadyOrdersMutation = () => {
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  const updateQueries = [QUERIES.IN_PROGRESS_ORDERS, orgSlug, restaurantId, branchId];
  return useMutation(
    (payload: OrderUpdateDto) =>
      tenantOrderUpdate({ orgSlug, restaurantId, branchId }, payload.id, {
        id: payload.id,
        status: OrderStatusEnum.READY,
      }),
    {
      onMutate: async (data) => {
        const previousOrders = queryClient.getQueryData(updateQueries);
        await queryClient.cancelQueries({ queryKey: updateQueries });
        queryClient.setQueryData(updateQueries, (old: any) => ({
          ...old,
          data: old.data.filter((item: OrderEntity) => item.id !== data.id),
        }));
        return { previousOrders };
      },
      onError: async () => {
        return queryClient.resetQueries({ queryKey: updateQueries });
      },
    }
  );
};

export const useCancelOrdersMutation = () => {
  const queryClient = useQueryClient();
  const { orgSlug, restaurantId, branchId } = useTenantContext();
  return useMutation(
    (payload: OrderUpdateDto) => tenantOrderUpdate({ orgSlug, restaurantId, branchId }, payload.id, payload),
    {
      onSuccess: async () => {
        return queryClient.refetchQueries([QUERIES.ORDERS, orgSlug, restaurantId, branchId]);
      },
    }
  );
};
